import { createShellNavigation, labels, toneLabels } from './ui/33-shell-navigation.mjs';
import { createLearning } from './ui/32-learning.mjs';
import { createStartup } from './ui/29-startup.mjs';
import { PHOTO_NAMES as imageNames } from './06-media/04-packs.mjs';
import { createAPI } from './ui/20-api-client.mjs';
import { createGames } from './features/19-games.mjs';
import { createCourses } from './features/17-courses.mjs';
import { bindRouteContinuity, bindThumbViews, ROUTE_FEATURES } from './ui/12-thumb-views.mjs';
import { bindHeaderSearch } from './ui/04-header-search.mjs';
import { createMutations } from './ui/03-mutations.mjs';
import { createUi } from './ui/01-dom.mjs';
import { createReading } from './features/10-reading.mjs';
import { createReferrals } from './features/18-referrals.mjs';
import { createReferralHandoff } from './ui/23-referral-handoff.mjs';
import { createAuth, renderLoginShell } from './features/08-auth.mjs';
import { createAdmin } from './features/09-admin.mjs';
import { createFeedView } from './features/01-feed-view.mjs';
import { createPosts } from './features/02-posts.mjs';
import { createComposer } from './features/03-composer.mjs';
import { createPeople } from './features/04-people.mjs';
import { createCollections } from './features/06-collections.mjs';
import { createConversations } from './features/05-conversations.mjs';
import { createSearch } from './features/12-search.mjs';
import { createExplore } from './features/15-explore.mjs';
import { createOnboarding, accountDestination } from './features/13-onboarding.mjs';
import { createStarterTour } from './features/14-starter-tour.mjs';
import { createSettings } from './features/07-settings.mjs';
import { mountModeWheel, clearReactViews } from './02-react-host.tsx';
import { EXPERIENCES, swipeMode } from '/experience-policy.mjs';
import { setLocale, uiText, translate, currentLocale, LOCALES } from '/04-i18n.mjs';
import { publicLocale, rememberPublicLocale } from '/04-i18n.mjs';
import { MEDIA_LIMITS } from '/media-policy.mjs';
import { decodePhoto, renderFrame, blobData } from '/06-media/02-render.mjs';
import { MediaEditor } from '/06-media/01-editor.mjs';
import {
  FeedSession,
  pullDistance,
  FEED_DISCOVERY_VERSION,
  validateDiscoveryResponse,
} from '/03-feed-session.mjs';
import { sharedInterests, interests } from '/social.mjs';
import { icon } from '/icons.mjs';
import { automaticTarget, BALANCED } from '/feed.js';

const {
  e,
  append,
  link,
  avatar,
  time,
  field,
  toast,
  dialog,
  contextMenu,
  closeContextMenu,
  button,
  sectionTop,
  iconButton,
  heading,
  empty,
  photoSource,
  photoSet,
} = createUi({ uiText, icon });

// Server snapshots own persistent data. Only routing, search, dialogs, and the current
// reading order live here; rendered text is created with textContent, never interpolated HTML.
let state;
let mode = 'automatic';
let feedScope = 'all';
let contentScope = 'ordinary';
let experience = 'wall';
let savedCollection = 'all';
let query = '';
const feed = new FeedSession(
  (mode, cursor, { limit }) =>
    api(
      `/api/feed?mode=${mode}&scope=${feedScope}&format=${experience}&contentScope=${contentScope}&limit=${limit}${cursor ? '&cursor=' + encodeURIComponent(cursor) : ''}`,
    ).then((page) => validateDiscoveryResponse(page, contentScope, FEED_DISCOVERY_VERSION)),
  { currentSnapshot: () => state },
);
let feedScroll = 0;
let previousPage;
let feedObserver;
let seenObserver = null;
const exposureTimers = new Map();
const pendingSeen = new Set();
let seenFlushTimer;
let positionSaveTimer;
let refreshPending = false;
let discoveryScope = 'new';
let discoveryInterest = '';
let discoveryShared = false;
let discoveryGeneration = 0;

const view = document.querySelector('#view');
const context = document.querySelector('#context');
const searchToggle = document.querySelector('#search-toggle');
const modal = document.querySelector('#modal');

document.querySelector('.skip').addEventListener('click', (event) => {
  event.preventDefault();
  const main = document.querySelector('#main');
  main.focus();
  main.scrollIntoView({ block: 'start', behavior: 'instant' });
});

const who = (id) => state.users.find((u) => u.id === id);

const api = createAPI({
  actor: () => state?.me.id,
  onUnauthorized: () => {
    state = undefined;
    showLogin();
  },
});
function command(input, options) {
  return mutate(input, options);
}

const referralHandoff = createReferralHandoff();
function route() {
  const parts = (location.hash.slice(2).split('?')[0] || 'feed').split('/');
  if (parts[1]) parts[1] = state?.routeAliases?.[parts[1]] ?? parts[1];
  return parts;
}
function go(path) {
  if (location.hash === `#/${path}`) render();
  else location.hash = `#/${path}`;
}
document.querySelector('#modal-close').addEventListener('click', () => modal.close());
modal.addEventListener('click', (event) => {
  if (event.target.closest('a[href^="#/"]')) modal.close();
});
modal.addEventListener('click', (event) => {
  if (event.target === modal) {
    const r = modal.getBoundingClientRect();
    if (
      event.clientX < r.left ||
      event.clientX > r.right ||
      event.clientY < r.top ||
      event.clientY > r.bottom
    )
      modal.close();
  }
});

// Composition root: live snapshot access and explicit callbacks prevent stale state
// and circular imports. Persistent values are always replaced by server responses.
const featureContext = {
  get seenObserver() {
    return seenObserver;
  },
  set seenObserver(value) {
    seenObserver = value;
  },
  pullDistance,
  FeedSession,
  get previousPage() {
    return previousPage;
  },
  set previousPage(value) {
    previousPage = value;
  },
  get refreshPending() {
    return refreshPending;
  },
  set refreshPending(value) {
    refreshPending = value;
  },
  get positionSaveTimer() {
    return positionSaveTimer;
  },
  set positionSaveTimer(value) {
    positionSaveTimer = value;
  },
  get seenFlushTimer() {
    return seenFlushTimer;
  },
  set seenFlushTimer(value) {
    seenFlushTimer = value;
  },
  pendingSeen,
  exposureTimers,
  BALANCED,
  EXPERIENCES,
  LOCALES,
  MediaEditor,
  profileImageTools: { decodePhoto, renderFrame, blobData, limits: MEDIA_LIMITS },
  api,
  navigationKey: () => location.hash,
  append,
  automaticTarget,
  avatar,
  button,
  get collectionDialog() {
    return collectionDialog;
  },
  command,
  get composer() {
    return composer;
  },
  context,
  contextMenu,
  currentLocale,
  setLocale,
  dialog,
  get discoveryGeneration() {
    return discoveryGeneration;
  },
  set discoveryGeneration(value) {
    discoveryGeneration = value;
  },
  get discoveryInterest() {
    return discoveryInterest;
  },
  set discoveryInterest(value) {
    discoveryInterest = value;
  },
  get discoveryScope() {
    return discoveryScope;
  },
  set discoveryScope(value) {
    discoveryScope = value;
  },
  get discoveryShared() {
    return discoveryShared;
  },
  set discoveryShared(value) {
    discoveryShared = value;
  },
  e,
  get editProfile() {
    return editProfile;
  },
  empty,
  get experience() {
    return experience;
  },
  set experience(value) {
    experience = value;
  },
  feed,
  discoveryPolicyVersion: FEED_DISCOVERY_VERSION,
  get contentScope() {
    return contentScope;
  },
  set contentScope(value) {
    contentScope = value;
  },
  get feedObserver() {
    return feedObserver;
  },
  set feedObserver(value) {
    feedObserver = value;
  },
  get feedScope() {
    return feedScope;
  },
  set feedScope(value) {
    feedScope = value;
  },
  get feedScroll() {
    return feedScroll;
  },
  set feedScroll(value) {
    feedScroll = value;
  },
  field,
  get flushSeen() {
    return flushSeen;
  },
  go,
  heading,
  icon,
  iconButton,
  imageNames,
  interests,
  labels,
  link,
  modal,
  get mode() {
    return mode;
  },
  set mode(value) {
    mode = value;
  },
  get modeDescription() {
    return modeDescription;
  },
  get modeWheel() {
    return modeWheel;
  },
  mountModeWheel,
  get observeReading() {
    return observeReading;
  },
  get paging() {
    return paging;
  },
  set paging(value) {
    paging = value;
  },
  get pagingTask() {
    return pagingTask;
  },
  set pagingTask(value) {
    pagingTask = value;
  },
  photoSet,
  photoSource,
  get updatePostCard() {
    return updatePostCard;
  },
  get refreshMediaPlayback() {
    return refreshMediaPlayback;
  },
  get postCard() {
    return postCard;
  },
  get query() {
    return query;
  },
  set query(value) {
    query = value;
  },
  get refreshFeed() {
    return refreshFeed;
  },
  render,
  get renderContext() {
    return renderContext;
  },
  route,
  get savedCollection() {
    return savedCollection;
  },
  set savedCollection(value) {
    savedCollection = value;
  },
  sectionTop,
  signOut,
  featureEnabled,
  sharedInterests,
  get state() {
    return state;
  },
  set state(value) {
    state = value;
  },
  get switchingExperience() {
    return switchingExperience;
  },
  set switchingExperience(value) {
    switchingExperience = value;
  },
  time,
  toast,
  toneLabels,
  translate,
  uiText,
  view,
  who,
};
const {
  modeDescription,
  chooseExperience,
  modeWheel,
  headerPreferences,
  renderFeed,
  canReuseFeedSurface,
  disposeFeedWarmup,
} = createFeedView(featureContext);
const updateThumbViews = bindThumbViews(featureContext, chooseExperience);
const { postCard, renderReels, updatePostCard, refreshMediaPlayback, disposeMediaPlayback } =
  createPosts(featureContext);
const { composer } = createComposer(featureContext);
const { renderPeople, renderProfile, editProfile } = createPeople(featureContext);
const referrals = createReferrals(featureContext, referralHandoff);
const {
  renderLearningHeader,
  renderLearningContext,
  renderCourses,
  renderContentScope,
  disposeLearning,
} = createLearning(featureContext);
Object.assign(featureContext, { renderLearningHeader, renderLearningContext, renderContentScope });
const { renderCourse, disposeCourse } = createCourses(featureContext);
const { renderSearch } = createSearch(featureContext);
const { renderExplore, disposeExplore } = createExplore(featureContext);
const { renderGames, disposeGames } = createGames(featureContext);
const { renderSaved, collectionDialog } = createCollections(featureContext);
const { renderCircles, renderNotifications, renderMessages } = createConversations(featureContext);
const { applyShellLanguage, renderSettings, renderContext } = createSettings(featureContext);

const {
  clearExposure,
  saveFeedPosition,
  flushSeen,
  observeReading,
  refreshFeed,
  isGestureControl,
} = createReading(featureContext);
const mutate = createMutations(featureContext);
const { renderOnboarding, renderVerification } = createOnboarding(featureContext);
const { renderStarterTour, disposeStarterTour } = createStarterTour(featureContext);
const { renderProviders, bindAccounts, renderCopy } = createAuth(
  Object.assign(featureContext, { publicLocale, rememberPublicLocale }),
);
const { renderAdminDashboard } = createAdmin(
  Object.assign(featureContext, {
    refreshState: async () => {
      state = await api('/api/state');
      feed.reset(mode);
      render();
    },
  }),
);

const startup = createStartup(featureContext);
featureContext.ensureFullState = startup.ensureFullState;
function showLogin() {
  startup.dispose();
  disposeFeedWarmup();
  disposeExplore();
  disposeGames();
  disposeLearning();
  disposeCourse();
  disposeStarterTour();
  disposeMediaPlayback();
  clearReactViews();
  void renderProviders();
  renderLoginShell(featureContext, searchToggle);
  referrals.mountPublic();
  feedScope = 'all';
  contentScope = 'ordinary';
  experience = 'wall';
  savedCollection = 'all';
  feed.reset(mode);
  feedObserver?.disconnect();
  seenObserver?.disconnect();
  clearExposure();
  pendingSeen.clear();
  clearTimeout(positionSaveTimer);
  document.documentElement.removeAttribute('data-theme');
  modal.close();
  view.replaceChildren();
  context.replaceChildren();
}
async function signOut() {
  await api('/api/logout', {});
  state = undefined;
  query = '';
  document.querySelector('#search').value = '';
  document.querySelector('#password').value = '';
  showLogin();
  toast('Signed out.');
}
// Snapshot flags inform presentation; all actions remain server-authorized.
function featureEnabled(key) {
  return state?.features ? state.features[key] === true : true;
}
const shell = createShellNavigation(featureContext, {
  applyShellLanguage,
  headerPreferences,
  modeWheel,
  ROUTE_FEATURES,
});
bindAccounts(featureContext, () =>
  go(accountDestination(location.hash, state.account, featureContext.referralDestination())),
);
bindHeaderSearch(featureContext);
document.querySelector('#compose-nav').addEventListener('click', () => composer());
bindRouteContinuity(featureContext, saveFeedPosition);
window.addEventListener(
  'scroll',
  () => {
    if (state && ['feed', 'learning'].includes(route()[0])) {
      feedScroll = Math.max(0, window.scrollY);
      clearTimeout(positionSaveTimer);
      positionSaveTimer = setTimeout(saveFeedPosition, 500);
    }
  },
  { passive: true },
);
let switchingExperience = false;

let interfaceTouch;
view.addEventListener(
  'touchstart',
  (event) => {
    interfaceTouch = null;
    if (
      !state ||
      !['feed', 'learning', 'circles'].includes(route()[0]) ||
      modal.open ||
      event.touches.length !== 1 ||
      isGestureControl(event.target) ||
      event.target.closest(
        'a,button,input,textarea,select,video,audio,summary,label,.stories,.feeling-canvas,.media-items,.mode-wheel,.mode-wheel-host,.media-editor,.conversation',
      )
    )
      return;
    interfaceTouch = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  },
  { passive: true },
);
view.addEventListener(
  'touchend',
  (event) => {
    if (!interfaceTouch) return;
    const touch = event.changedTouches[0],
      current = route()[0] === 'circles' ? 'circles' : experience,
      next = swipeMode(current, touch.clientX - interfaceTouch.x, touch.clientY - interfaceTouch.y);
    interfaceTouch = null;
    if (next !== current) chooseExperience(next);
  },
  { passive: true },
);
view.addEventListener('touchcancel', () => (interfaceTouch = null), {
  passive: true,
});

let paging = false;
let pagingTask;

function render() {
  const destination = route()[0];
  const nextScope =
    destination === 'learning' || destination === 'courses'
      ? 'learning'
      : destination === 'feed'
        ? 'ordinary'
        : contentScope;
  if (nextScope !== contentScope) {
    contentScope = nextScope;
    feed.reset(mode);
    feedScroll = 0;
  }
  if (state && canReuseFeedSurface()) {
    closeContextMenu();
    shell();
    updateThumbViews();
    renderFeed();
    return;
  }
  if (!['feed', 'learning'].includes(route()[0]) || !state) disposeFeedWarmup();
  disposeExplore();
  disposeGames();
  disposeLearning();
  disposeCourse();
  referrals.dispose();
  disposeStarterTour();
  const focusKey = document.activeElement?.dataset.focusKey;
  closeContextMenu();
  disposeMediaPlayback();
  clearReactViews(true);
  delete view.dataset.experience;
  for (const player of document.querySelectorAll('#view video,#view audio')) player.pause();
  if (!state) return showLogin();
  feedObserver?.disconnect();
  seenObserver?.disconnect();
  clearExposure();
  const experienceFlag = { visual: 'visual-feed', talk: 'talk-feed' }[experience];
  if (experienceFlag && !featureEnabled(experienceFlag)) {
    experience = 'wall';
    feed.reset(mode);
  }
  if (!featureEnabled('adaptive-feed') && mode !== 'latest') {
    mode = 'latest';
    feed.reset(mode);
  }
  previousPage = route()[0];
  shell();
  updateThumbViews();
  renderContext();
  view.replaceChildren();
  const [page, id] = route();
  document.querySelectorAll('#mobile-nav a').forEach((a) => {
    if (a.hash === `#/${page}`) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  });
  const routeFlag = ROUTE_FEATURES[page];
  if (routeFlag && !featureEnabled(routeFlag)) {
    view.append(
      empty('This feature is unavailable on this server.'),
      link('Back to your wall', '#/feed', 'secondary-button'),
    );
    return;
  }
  if (startup.guardRoute()) return;
  switch (page) {
    case 'learning':
    case 'feed':
      renderFeed();
      break;
    case 'games':
      renderGames();
      break;
    case 'reels':
      renderReels();
      break;
    case 'circles':
      renderCircles(id);
      break;
    case 'friends':
      renderPeople();
      break;
    case 'search':
      renderSearch();
      break;
    case 'explore':
      renderExplore();
      break;
    case 'discover':
      renderPeople(true);
      break;
    case 'courses':
      renderCourses();
      break;
    case 'course':
      renderCourse(id);
      break;
    case 'saved':
      renderSaved();
      break;
    case 'profile':
      renderProfile(id ?? state.me.id);
      break;
    case 'notifications':
      renderNotifications();
      break;
    case 'messages':
      renderMessages(id);
      break;
    case 'settings':
      renderSettings();
      break;
    case 'admin':
      renderAdminDashboard();
      break;
    case 'starter':
      renderStarterTour();
      break;
    case 'welcome':
      renderOnboarding();
      break;
    case 'invitation':
    case 'stop-invitations':
      void referrals.renderRecipient();
      break;
    case 'verify-email':
      renderVerification();
      break;
    case 'post': {
      const post = state.posts.find((p) => p.id === id);
      view.append(link('← Back to your wall', '#/feed', 'text-button'));
      view.append(
        post
          ? postCard(post, { expanded: true })
          : empty('This post is unavailable, private, or has been removed.'),
      );
      break;
    }
    default:
      view.append(
        heading('A quiet corner.', 'This page could not be found.'),
        link('Back to your wall', '#/feed', 'secondary-button'),
      );
  }
  refreshMediaPlayback();
  if (focusKey) {
    requestAnimationFrame(() => {
      const target = [...document.querySelectorAll('[data-focus-key]')].find(
        (node) => node.dataset.focusKey === focusKey,
      );
      target?.focus({ preventScroll: true });
    });
  }
}
document
  .querySelectorAll('[data-lucide]')
  .forEach((node) => node.replaceWith(icon(node.dataset.lucide)));
await startup.initialize({ renderCopy, accountDestination, referralHandoff, showLogin });

setInterval(() => {
  if (!state) return;
  const expired =
    state.posts.some((p) => p.expiresAt && p.expiresAt <= Date.now()) ||
    state.stories.some((p) => p.expiresAt <= Date.now());
  if (expired) {
    state.posts = state.posts.filter((p) => !p.expiresAt || p.expiresAt > Date.now());
    state.stories = state.stories.filter((p) => p.expiresAt > Date.now());
    render();
  }
}, 15000);
